<template>
  <div class="note-container" :class="{ 'note-highlight': highlight }" :style="{ 'padding-left': indentLevel }">
    <div
      class="note"
      ref="note"
      @mouseenter="$emit('note-mouseenter', note.id)"
      @mouseleave="$emit('note-mouseleave', note.id)"
    >
    <Popper :content="note.username" placement="top" hover>
      <div
        class="d-none d-lg-block note-dot"
        :title="username"
        :class="(this.editing || this.note.unsaved) && 'editing'"
      >
        {{ initials }}
      </div>
    </Popper>
      <transition :name="transitionName" mode="out-in">
        <div v-if="isEditing" key="editing" class="note-edit">
          <div class="note-anon-username" v-if="needsUsername">
            <input
              type="text"
              class="text-note form-control"
              @keyup.enter="enterUsername"
              v-model="anonUsername"
              placeholder="enter your full name to add a mix note"
            />
          </div>
          <div class="note-edit-container">
            <input
              type="text"
              ref="input"
              class="text-note form-control w-auto flex-grow-1"
              @keyup.enter="finishEditing"
              v-model="editText"
              @focus="noteFocus"
              placeholder="enter mix notes here..."
            />
            <div v-if="showButtons" class="ms-0 ms-md-2 mt-2 mt-md-0">
              <button
                type="button"
                :disabled="editText == null || editText.length == 0 || (needsUsername && !anonUsername) || saving"
                @click.prevent="finishEditing"
                class="btn btn-dark"
              >
                <font-awesome-icon v-if="saving" icon="spinner" spin />
                <span v-else>Save</span>
              </button>
              <button type="button" @click.prevent="cancelEditing" :disabled="saving" class="btn btn-dark ms-1">
                Cancel
              </button>
            </div>
          </div>
        </div>
        <div v-else key="display" class="col w-100 mix-note-text-group pl-0 pl-lg-1">
          <note-text
            :note-content="note.note"
            :completed="note.completed"
            :number="note.number"
            :color="note.color"
            @time-click="noteTimeClick"
          />
          <Popper v-if="likes.length > 0" :content="reactionText('like')" hover>
            <div class="mix-note-reactions">
              <span class="reaction-dot"
                ><font-awesome-icon icon="thumbs-up" />
                <div class="reaction-count" v-if="reactionCount('like') > 1">{{ reactionCount("like") }}</div>
              </span>
            </div>
          </Popper>
          <div class="mix-note-extra-info">
            <div class="mix-note-controls">
              <LikeButton @like="doLike" />
              <a v-if="canDone && !note.completed" href="#" @click.prevent="toggleDone"
                ><font-awesome-icon icon="check" />Done</a
              >
              <a href="#" @click.prevent="replyClick"><font-awesome-icon icon="reply" />Reply</a>
              <a href="#" v-if="canEdit" @click.prevent="startEdit"><font-awesome-icon icon="edit" />Edit</a>
              <a href="#" v-if="canDelete" @click.prevent="deleteNote"><font-awesome-icon icon="trash" />Delete</a>
            </div>
            <div class="mix-note-text-date d-flex align-items-center">
              <Popper :content="'' + note.created_at" hover>
                <span>
                  <span class="mix-note-username">{{ note.username }}, </span>
                  <span class="me-2">{{ noteDate }} ago </span>
                </span>
              </Popper>
              <span class="me-2" v-if="version">(Version {{ version }})</span>
          </div>
          </div>
        </div>
      </transition>
    </div>
    <note
      v-for="n in replies"
      :indent="indent + 1"
      :note="n"
      :owns-mix="ownsMix"
      :version="version"
      :key="n.id"
    />
  </div>
</template>

<style>
/* Transition TO edit mode: display fades out slow, edit fades in quick */
.to-edit-leave-active {
  transition: opacity 0.3s;
}
.to-edit-enter-active {
  transition: opacity 0.05s;
}
.to-edit-enter,
.to-edit-leave-to {
  opacity: 0;
}

/* No transition when returning from edit to display */
.no-transition-enter-active,
.no-transition-leave-active {
  transition: none;
}
</style>

<style scoped>
.mix-note-text-group {
  font-size: 15px;
  font-family: arial;
}
.mix-note-text-group > div {
  margin-bottom: 3px;
}

.mix-note-extra-info {
  display: flex;
}

.mix-note-username {
  display: none;
}


@media (max-width: 576px) {
  .mix-note-extra-info {
    display: block;
  }

  .mix-note-username {
    display: inline-block;
  }
}

.mix-note-text-date {
  font-size: 90%;
  color: #aaaaaa;
  font-style: italic;
}

.mix-note-reactions {
  color: #ff6a00;
  font-size: 120%;
  position: relative;
  margin-left: 5px;
}

.reaction-dot {
  font-size: 20px;
}

.reaction-count {
  position: absolute;
  bottom: -3px;
  right: -2px;
  color: #444;
  font-size: 12px;
  font-weight: bold;
  font-style: normal;
}

.note {
  padding-top: 5px;
  padding-bottom: 5px;
  display: flex;
  align-items: center;
}

.note-highlight .mix-note-all-text {
  background: #ffdfaf;
  border-radius: 5px;
}

.note-edit {
  flex-grow: 1;
}

.note-anon-username {
  flex-grow: 1;
  margin-bottom: 0.5rem;
}

.note-edit-container {
  display: flex;
  flex-wrap: wrap;
}

.note-dot {
  line-height: 35px;
  text-align: center;
  height: 35px;
  width: 35px;
  border-radius: 50%;
  font-size: 15px;
  font-weight: bold;
  margin-left: 5px;
  margin-right: 10px;
  padding: 0px;
  background-color: white;
  border: 1px solid #ccc;
  font-family: monospace;
  color: #333;
  cursor: default;
}

.note-dot.editing {
  background-color: #ff6a00;
  color: white;
  border: 0;
}

.note-add-button {
  line-height: 1;
  margin-left: 5px;
  margin-bottom: 10px;
  margin-top: 10px;
  background-color: white;
}

.show-completed {
  padding-top: 12px;
  padding-left: 67px;
  width: auto;
}
</style>

<style>
.mix-note-controls {
  display: flex;
}

.mix-note-controls a {
  font-family: arial;
  color: #888;
  text-decoration: none;
  margin-right: 10px;
}

.mix-note-controls svg {
  margin-right: 2px;
}

.note-dropdown button {
  line-height: 1;
  padding: 0.15rem 0.5rem;
  color: #666;
  border-color: #666;
}
</style>

<script>
import { parseJSON, formatDistance } from "date-fns"
import timeFormat from "../utils/time_format"
import LikeButton from "./like_button.vue"
import NoteText from "./note_text.vue"
import noteStore from "./note_store.js"

export default {
  props: ["note", "currentTime", "ownsMix", "indent", "version", "highlight"],
  components: { LikeButton, NoteText },
  name: "note",
  data: function () {
    return {
      anonUsername: "",
      editText: this.note.note,
      editing: false,
      saving: false,
      savedNote: "",
      showButtons: !!this.note.parent_note_id,
    }
  },
  methods: {
    noteFocus() {
      if (this.editText) return
      if (this.note.parent_note_id) return

      if (this.currentTime > 0) {
        this.editText = `(${timeFormat(this.currentTime, 0)}) `
      }
      this.showButtons = true
    },
    startEdit() {
      this.savedNote = this.note.note
      this.editText = this.savedNote
      this.editing = true
      this.showButtons = true
    },
    cancelEditing() {
      if (this.note.parent_note_id && this.note.unsaved) return this.deleteNote()
      else if (this.note.unsaved) {
        this.editText = ""
      }

      this.editing = false
      this.showButtons = false
    },
    noteBlur() {
      this.showButtons = false
    },
    deleteNote() {
      noteStore.deleteNote(this.note.id)
    },
    enterUsername() {
      if (this.anonUsername) {
        this.note.username = this.anonUsername
        window.localStorage.username = this.anonUsername
        this.anonUsername = ""
      }
    },
    async finishEditing(event) {
      this.enterUsername()

      if (this.note.id) this.editing = false

      this.saving = true
      if (this.note.unsaved) {
        await noteStore.createNote({ ...this.note, note: this.editText })
      } else {
        await noteStore.updateNote(this.note.id, { note: this.editText })
      }
      this.saving = false
    },
    toggleDone() {
      noteStore.updateNote(this.note.id, { completed: !this.note.completed })
    },
    noteTimeClick(numericTime) {
      this.$emit("play-at-time", numericTime)
    },
    replyClick() {
      noteStore.replyTo(this.note)
    },
    doLike() {
      noteStore.createNote({ note: ":like:", parent_note_id: this.note.id, mix_id: this.note.mix_id, isLike: true })
    },
    reactionUsernames(reaction) {
      let reactions = this.likes
      let users = {}
      if (reactions.length == 0) return []

      for (let n of reactions) {
        users[n.username || "anonymous"] = 1
      }

      return Object.keys(users)
    },
    reactionText(reaction) {
      let sortedNames = this.reactionUsernames(reaction).sort()

      return "Liked by " + sortedNames.join(", ")
    },
    reactionCount(reaction) {
      return this.reactionUsernames(reaction).length
    },
  },
  mounted() {
    if (this.note.unsaved && this.note.parent_note_id) {
      this.$nextTick().then(() => this.$refs["input"].focus())
    }
  },
  computed: {
    transitionName() {
      // Use 'to-edit' transition when entering edit mode, 'no-transition' when exiting
      return this.isEditing ? "to-edit" : "no-transition"
    },
    indentLevel() {
      return this.indent * 15 + "px"
    },
    isEditing() {
      return this.editing || this.note.unsaved
    },
    loggedIn() {
      return mmmGlobals.logged_in
    },
    needsUsername() {
      return !this.note.username && !window.localStorage.username
    },
    username() {
      return this.note.username || this.anonUsername || window.localStorage.username
    },
    initials() {
      let username = this.username
      if (username) {
        var split = username.split(/\s+/).filter(s => s.length > 0)
        if (split.length > 1) return (split[0][0] + split[split.length - 1][0] || "").toUpperCase()
        else return username[0].toUpperCase()
      } else {
        return "?"
      }
    },
    canEdit() {
      if (mmmGlobals.user_id == -1) return false

      return this.note.user_id == mmmGlobals.user_id
    },
    canDone() {
      if (mmmGlobals.user_id == -1) return false

      return !this.note.parent_note_id && (this.note.user_id == mmmGlobals.user_id || this.ownsMix)
    },
    canDelete() {
      if (mmmGlobals.user_id == -1) return false

      return this.note.user_id == mmmGlobals.user_id || this.ownsMix
    },
    noteDate() {
      const date = parseJSON(this.note.created_at)
      return formatDistance(date, new Date())
    },
    replies() {
      return noteStore.replies[this.note.id] || []
    },
    likes() {
      return noteStore.likes[this.note.id] || []
    },
  },
}
</script>
